import {memo, useState} from 'react'
import {SelectProvider} from '@ariakit/react'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Switch} from '@/components/commons/switch/Switch'
import {SelectItem, SelectList, SelectPopover, SelectTrigger} from '@/components/ui/select-atoms/SelectAtoms'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {EXPORT_FORMATS, EXPORT_STATUS_FILTERS, ExportFormat, ExportStatusFilter} from '@/features/exports/types'
import {statusLabelKey} from '@/features/exports/utils/status'
import {
    EXPORT_DATE_PRESET_OPTIONS,
    ExportDatePreset,
    periodLabelKey,
    resolvePresetRange
} from '@/features/exports/utils/period'
import {StyledFilterField, StyledRemovableChip} from './style'

interface ExportsFiltersProps {
    /** Localised label of the selected data type; undefined when viewing all entities. */
    dataTypeLabel?: string
    onDataTypeClear: () => void
    status?: ExportStatusFilter
    onStatusChange: (status?: ExportStatusFilter) => void
    format?: ExportFormat
    onFormatChange: (format?: ExportFormat) => void
    dateFrom?: string
    dateTo?: string
    onDateFromChange: (value?: string) => void
    onDateToChange: (value?: string) => void
    includeArchived: boolean
    onIncludeArchivedChange: (value: boolean) => void
}

const ExportsFiltersComponent = ({
    dataTypeLabel,
    onDataTypeClear,
    status,
    onStatusChange,
    format,
    onFormatChange,
    onDateFromChange,
    onDateToChange,
    includeArchived,
    onIncludeArchivedChange
}: ExportsFiltersProps) => {
    const {t} = useTranslation()
    const [preset, setPreset] = useState<ExportDatePreset>('all')

    // Sentinel for the "all" options so the trigger renders them as a real value, not the placeholder.
    const STATUS_ALL = 'all'
    const FORMAT_ALL = 'all'

    const statusText = (value: string) => {
        const key = statusLabelKey(value)
        return key ? t(key) : value
    }

    // File extensions are language-neutral; show them uppercased (matching the export rows).
    const formatText = (value: string) => value.toUpperCase()

    const handleStatusChange = (value: string) =>
        onStatusChange(value === STATUS_ALL ? undefined : (value as ExportStatusFilter))

    const handleFormatChange = (value: string) =>
        onFormatChange(value === FORMAT_ALL ? undefined : (value as ExportFormat))

    const handlePresetChange = (value: string) => {
        const next = value as ExportDatePreset
        setPreset(next)
        const {dateFrom: from, dateTo: to} = resolvePresetRange(next)
        onDateFromChange(from)
        onDateToChange(to)
    }

    const clearPeriod = () => {
        setPreset('all')
        onDateFromChange(undefined)
        onDateToChange(undefined)
    }

    const clearFilters = () => {
        onDataTypeClear()
        onStatusChange(undefined)
        onFormatChange(undefined)
        clearPeriod()
        onIncludeArchivedChange(false)
    }

    const dataTypeActive = dataTypeLabel !== undefined
    const statusActive = status !== undefined
    const formatActive = format !== undefined
    const periodActive = preset !== 'all'
    const hasActiveFilters = dataTypeActive || statusActive || formatActive || periodActive

    return (
        <Flexbox direction="column" gap={3} fullWidth>
            <Flexbox align="flex-end" gap={2} fullWidth style={{flexWrap: 'wrap'}}>
                <StyledFilterField direction="column" gap={1}>
                    <Text size="textXs" palette="neutral" shade="500">
                        {t('exports:filter_status')}
                    </Text>
                    <SelectProvider
                        value={status ?? STATUS_ALL}
                        setValue={value => handleStatusChange(value as string)}
                    >
                        <SelectTrigger
                            fullWidth
                            placeholder={t('exports:filter_all_statuses')}
                            displayValue={value =>
                                value === STATUS_ALL ? t('exports:filter_all_statuses') : statusText(value as string)
                            }
                        />
                        <SelectPopover>
                            <SelectList>
                                <SelectItem value={STATUS_ALL} hasItemCheckbox>
                                    {t('exports:filter_all_statuses')}
                                </SelectItem>
                                {EXPORT_STATUS_FILTERS.map(value => (
                                    <SelectItem key={value} value={value} hasItemCheckbox>
                                        {statusText(value)}
                                    </SelectItem>
                                ))}
                            </SelectList>
                        </SelectPopover>
                    </SelectProvider>
                </StyledFilterField>

                <StyledFilterField direction="column" gap={1}>
                    <Text size="textXs" palette="neutral" shade="500">
                        {t('exports:filter_format')}
                    </Text>
                    <SelectProvider
                        value={format ?? FORMAT_ALL}
                        setValue={value => handleFormatChange(value as string)}
                    >
                        <SelectTrigger
                            fullWidth
                            placeholder={t('exports:filter_all_formats')}
                            displayValue={value =>
                                value === FORMAT_ALL ? t('exports:filter_all_formats') : formatText(value as string)
                            }
                        />
                        <SelectPopover>
                            <SelectList>
                                <SelectItem value={FORMAT_ALL} hasItemCheckbox>
                                    {t('exports:filter_all_formats')}
                                </SelectItem>
                                {EXPORT_FORMATS.map(value => (
                                    <SelectItem key={value} value={value} hasItemCheckbox>
                                        {formatText(value)}
                                    </SelectItem>
                                ))}
                            </SelectList>
                        </SelectPopover>
                    </SelectProvider>
                </StyledFilterField>

                <StyledFilterField direction="column" gap={1}>
                    <Text size="textXs" palette="neutral" shade="500">
                        {t('exports:filter_period')}
                    </Text>
                    <SelectProvider value={preset} setValue={value => handlePresetChange(value as string)}>
                        <SelectTrigger
                            fullWidth
                            placeholder={t('exports:filter_period_all')}
                            displayValue={value => t(periodLabelKey(value as ExportDatePreset))}
                        />
                        <SelectPopover>
                            <SelectList>
                                {EXPORT_DATE_PRESET_OPTIONS.map(value => (
                                    <SelectItem key={value} value={value} hasItemCheckbox>
                                        {t(periodLabelKey(value))}
                                    </SelectItem>
                                ))}
                            </SelectList>
                        </SelectPopover>
                    </SelectProvider>
                </StyledFilterField>
            </Flexbox>

            <Switch
                size="sm"
                label={t('exports:show_archived')}
                checked={includeArchived}
                onChange={onIncludeArchivedChange}
            />

            {hasActiveFilters && (
                <Flexbox align="center" gap={2} fullWidth style={{flexWrap: 'wrap'}}>
                    <Text size="textXs" palette="neutral" shade="500">
                        {t('exports:filter_active_label')}
                    </Text>
                    {dataTypeActive && dataTypeLabel && (
                        <StyledRemovableChip
                            role="button"
                            shape="rectangle"
                            size="sm"
                            variant="neutral"
                            onClick={onDataTypeClear}
                        >
                            {dataTypeLabel}
                            <XCloseIcon />
                        </StyledRemovableChip>
                    )}
                    {statusActive && status && (
                        <StyledRemovableChip
                            role="button"
                            shape="rectangle"
                            size="sm"
                            variant="neutral"
                            onClick={() => onStatusChange(undefined)}
                        >
                            {statusText(status)}
                            <XCloseIcon />
                        </StyledRemovableChip>
                    )}
                    {formatActive && format && (
                        <StyledRemovableChip
                            role="button"
                            shape="rectangle"
                            size="sm"
                            variant="neutral"
                            onClick={() => onFormatChange(undefined)}
                        >
                            {formatText(format)}
                            <XCloseIcon />
                        </StyledRemovableChip>
                    )}
                    {periodActive && (
                        <StyledRemovableChip
                            role="button"
                            shape="rectangle"
                            size="sm"
                            variant="neutral"
                            onClick={clearPeriod}
                        >
                            {t(periodLabelKey(preset))}
                            <XCloseIcon />
                        </StyledRemovableChip>
                    )}
                    <Text
                        render="span"
                        role="button"
                        size="textXs"
                        palette="primary"
                        shade="600"
                        style={{cursor: 'pointer'}}
                        onClick={clearFilters}
                    >
                        {t('exports:filter_clear')}
                    </Text>
                </Flexbox>
            )}
        </Flexbox>
    )
}

export const ExportsFilters = memo(ExportsFiltersComponent)
